import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, HeartPulse, Users } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import WellnessRing from "@/components/WellnessRing";

const IMAGES = {
  main: "/hero-community.jpg",
  small: "https://images.pexels.com/photos/6193559/pexels-photo-6193559.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

function HeroVisual({ yImg, yCard }) {
  const { t, isRtl } = useLanguage();
  return (
    <div className="lg:col-span-6 relative">
      <div className="absolute inset-0 -m-8 sm:-m-14 opacity-90 pointer-events-none">
        <WellnessRing className="w-full h-full" />
      </div>

      <motion.div
        style={{ y: yImg }}
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 mx-auto max-w-md"
      >
        <div className="rounded-[2.5rem] overflow-hidden shadow-[0_30px_80px_rgba(15,23,42,0.18)] border-[6px] border-white aspect-[4/5]">
          <img src={IMAGES.main} alt="Community practicing mindfulness and fitness together" className="w-full h-full object-cover object-center" data-testid="hero-main-image" />
        </div>
      </motion.div>

      <motion.div
        style={{ y: yCard }}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1 }}
        className={`absolute z-20 top-8 ${isRtl ? "-left-2 sm:-left-6" : "-right-2 sm:-right-6"} float-soft`}
      >
        <div className="flex items-center gap-3 bg-white/90 backdrop-blur-xl border border-slate-100 rounded-2xl px-4 py-3 shadow-xl">
          <span className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
            <HeartPulse size={20} className="text-lime-600" />
          </span>
          <span>
            <span className="block text-sm font-bold text-slate-900">{t.hero.card1Title}</span>
            <span className="block text-xs text-slate-500">{t.hero.card1Sub}</span>
          </span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.15 }}
        className={`absolute z-20 bottom-6 ${isRtl ? "-right-2 sm:-right-4" : "-left-2 sm:-left-4"} float-soft`}
        style={{ animationDelay: "1.5s" }}
      >
        <div className="flex items-center gap-3 bg-slate-900 text-white rounded-2xl px-4 py-3 shadow-2xl">
          <span className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
            <Users size={20} className="text-teal-300" />
          </span>
          <span>
            <span className="block text-sm font-bold">{t.hero.card2Title}</span>
            <span className="block text-xs text-slate-300">{t.hero.card2Sub}</span>
          </span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: isRtl ? -30 : 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, delay: 0.9 }}
        className="absolute z-0 -bottom-8 end-4 sm:end-0 w-32 sm:w-44 rounded-3xl overflow-hidden border-4 border-white shadow-xl hidden sm:block"
      >
        <img src={IMAGES.small} alt="Balance and calm" className="w-full h-32 sm:h-44 object-cover" />
      </motion.div>
    </div>
  );
}

export default function Hero({ onJoin }) {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" data-testid="home-section" ref={ref} className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] start-[-10%] w-[60vw] h-[60vw] rounded-full bg-lime-100/50 blur-3xl" />
        <div className="absolute bottom-[-30%] end-[-15%] w-[50vw] h-[50vw] rounded-full bg-teal-50 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-lime-50 text-lime-800 border border-lime-300 shadow-sm"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 rounded-full bg-lime-500 animate-pulse" />
            {t.hero.badge}
          </motion.div>

          <h1 className="mt-6 font-display font-extrabold tracking-tight leading-[1.25] text-4xl sm:text-5xl lg:text-6xl text-slate-900" data-testid="hero-title">
            {t.hero.titleLines.map((line, i) => (
              <span key={line} className="block overflow-hidden pt-4 -mt-4 pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-lime-500" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <motion.button
              data-testid="hero-join-waitlist-button"
              onClick={onJoin}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
              className="px-7 py-3.5 rounded-full bg-lime-500 text-slate-900 font-bold text-sm sm:text-base shadow-[0_10px_30px_rgba(132,204,22,0.35)] hover:bg-lime-400 transition-colors"
            >
              {t.hero.ctaPrimary}
            </motion.button>
            <a
              data-testid="hero-discover-button"
              href="#tafakar"
              className="px-7 py-3.5 rounded-full border-2 border-slate-900 text-slate-900 font-bold text-sm sm:text-base hover:bg-slate-900 hover:text-white transition-colors"
            >
              {t.hero.ctaSecondary}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
            className="mt-14 hidden lg:flex items-center gap-2 text-xs text-slate-400 font-medium"
          >
            <ArrowDown size={14} className="animate-bounce" />
            {t.hero.scroll}
          </motion.div>
        </div>

        <HeroVisual yImg={yImg} yCard={yCard} />
      </div>
    </section>
  );
}
